/**
 * Input Validation Utilities
 * ==========================
 * Provides consistent validation across forms and API inputs.
 *
 * Features:
 * - Composable validator functions
 * - Schema-based validation
 * - Pre-built schemas for common forms
 * - User-friendly error messages
 *
 * @module utils/validation
 */

import { ValidationError } from "./errors";

// ============================================================================
// INDIVIDUAL VALIDATORS
// ============================================================================

/**
 * Collection of reusable validator functions.
 * Each validator throws a ValidationError if validation fails.
 */
export const validators = {
  /**
   * Validates that a value is not empty.
   * @param {*} value - Value to validate
   * @param {string} fieldName - Field name for error message
   * @throws {ValidationError} If value is empty
   */
  required: (value, fieldName) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      throw new ValidationError("This field is required", fieldName, value);
    }
    return true;
  },

  /**
   * Creates a min length validator.
   * @param {number} min - Minimum length
   * @returns {Function} Validator function
   */
  minLength: (min) => (value, fieldName) => {
    if (value && value.length < min) {
      throw new ValidationError(
        `Must be at least ${min} character${min === 1 ? "" : "s"}`,
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Creates a max length validator.
   * @param {number} max - Maximum length
   * @returns {Function} Validator function
   */
  maxLength: (max) => (value, fieldName) => {
    if (value && value.length > max) {
      throw new ValidationError(
        `Must be no more than ${max} character${max === 1 ? "" : "s"}`,
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Creates a length range validator.
   * @param {number} min - Minimum length
   * @param {number} max - Maximum length
   * @returns {Function} Validator function
   */
  lengthBetween: (min, max) => (value, fieldName) => {
    if (value && (value.length < min || value.length > max)) {
      throw new ValidationError(
        `Must be between ${min} and ${max} characters`,
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Validates email format.
   */
  email: (value, fieldName) => {
    if (!value) return true; // Let required handle empty
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      throw new ValidationError("Invalid email format", fieldName, value);
    }
    return true;
  },

  /**
   * Validates URL format.
   */
  url: (value, fieldName) => {
    if (!value) return true;
    try {
      new URL(value);
      return true;
    } catch {
      throw new ValidationError("Invalid URL format", fieldName, value);
    }
  },

  /**
   * Validates alphanumeric with underscores and hyphens.
   */
  alphanumeric: (value, fieldName) => {
    if (!value) return true;
    const regex = /^[a-zA-Z0-9_-]+$/;
    if (!regex.test(value)) {
      throw new ValidationError(
        "Only letters, numbers, underscores, and hyphens allowed",
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Validates alphanumeric with spaces.
   */
  alphanumericSpaces: (value, fieldName) => {
    if (!value) return true;
    const regex = /^[a-zA-Z0-9\s_-]+$/;
    if (!regex.test(value)) {
      throw new ValidationError(
        "Only letters, numbers, spaces, underscores, and hyphens allowed",
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Creates a minimum number validator.
   * @param {number} min - Minimum value
   * @returns {Function} Validator function
   */
  min: (min) => (value, fieldName) => {
    if (value !== null && value !== undefined && value < min) {
      throw new ValidationError(`Must be at least ${min}`, fieldName, value);
    }
    return true;
  },

  /**
   * Creates a maximum number validator.
   * @param {number} max - Maximum value
   * @returns {Function} Validator function
   */
  max: (max) => (value, fieldName) => {
    if (value !== null && value !== undefined && value > max) {
      throw new ValidationError(
        `Must be no more than ${max}`,
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Creates a range validator for numbers.
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @returns {Function} Validator function
   */
  between: (min, max) => (value, fieldName) => {
    if (value !== null && value !== undefined && (value < min || value > max)) {
      throw new ValidationError(
        `Must be between ${min} and ${max}`,
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Validates integer.
   */
  integer: (value, fieldName) => {
    if (
      value !== null &&
      value !== undefined &&
      !Number.isInteger(Number(value))
    ) {
      throw new ValidationError("Must be a whole number", fieldName, value);
    }
    return true;
  },

  /**
   * Validates positive number.
   */
  positive: (value, fieldName) => {
    if (value !== null && value !== undefined && Number(value) <= 0) {
      throw new ValidationError("Must be a positive number", fieldName, value);
    }
    return true;
  },

  /**
   * Creates a regex pattern validator.
   * @param {RegExp} pattern - Regular expression
   * @param {string} message - Error message
   * @returns {Function} Validator function
   */
  pattern:
    (pattern, message = "Invalid format") =>
    (value, fieldName) => {
      if (value && !pattern.test(value)) {
        throw new ValidationError(message, fieldName, value);
      }
      return true;
    },

  /**
   * Creates an enum validator (value must be in allowed list).
   * @param {Array} allowedValues - List of allowed values
   * @returns {Function} Validator function
   */
  oneOf: (allowedValues) => (value, fieldName) => {
    if (value && !allowedValues.includes(value)) {
      throw new ValidationError(
        `Must be one of: ${allowedValues.join(", ")}`,
        fieldName,
        value,
      );
    }
    return true;
  },

  /**
   * Validates that value doesn't contain HTML/script tags.
   */
  noHtml: (value, fieldName) => {
    if (!value) return true;
    const htmlRegex = /<[^>]*>/g;
    if (htmlRegex.test(value)) {
      throw new ValidationError("HTML tags are not allowed", fieldName, value);
    }
    return true;
  },

  /**
   * Validates JSON string.
   */
  json: (value, fieldName) => {
    if (!value) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      throw new ValidationError("Invalid JSON format", fieldName, value);
    }
  },

  /**
   * Creates a custom validator.
   * @param {Function} fn - Function that returns true or throws
   * @param {string} message - Error message on failure
   * @returns {Function} Validator function
   */
  custom: (fn, message) => (value, fieldName) => {
    if (!fn(value)) {
      throw new ValidationError(message, fieldName, value);
    }
    return true;
  },
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate a single field with multiple rules.
 *
 * @param {*} value - Value to validate
 * @param {string} fieldName - Field name for error messages
 * @param {Function[]} rules - Array of validator functions
 * @returns {true} Returns true if all validations pass
 * @throws {ValidationError} If any validation fails
 *
 * @example
 * validateField(email, 'email', [validators.required, validators.email]);
 */
export const validateField = (value, fieldName, rules) => {
  for (const rule of rules) {
    rule(value, fieldName);
  }
  return true;
};

/**
 * Validate an entire object against a schema.
 * Returns all validation errors at once.
 *
 * @param {Object} data - Object to validate
 * @param {Object} schema - Validation schema { fieldName: [validators] }
 * @returns {{ isValid: boolean, errors: Object }} Validation result
 *
 * @example
 * const result = validateSchema(formData, {
 *   name: [validators.required, validators.minLength(2)],
 *   email: [validators.required, validators.email],
 * });
 *
 * if (!result.isValid) {
 *   console.log(result.errors); // { name: 'Must be at least 2 characters' }
 * }
 */
export const validateSchema = (data, schema) => {
  const errors = {};

  for (const [fieldName, rules] of Object.entries(schema)) {
    try {
      validateField(data[fieldName], fieldName, rules);
    } catch (error) {
      if (error instanceof ValidationError) {
        errors[fieldName] = error.message;
      } else {
        throw error;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Validate and return first error only.
 * Useful for immediate feedback.
 *
 * @param {Object} data - Object to validate
 * @param {Object} schema - Validation schema
 * @returns {{ isValid: boolean, firstError: string|null, field: string|null }}
 */
export const validateFirst = (data, schema) => {
  for (const [fieldName, rules] of Object.entries(schema)) {
    try {
      validateField(data[fieldName], fieldName, rules);
    } catch (error) {
      if (error instanceof ValidationError) {
        return {
          isValid: false,
          firstError: error.message,
          field: fieldName,
        };
      }
      throw error;
    }
  }

  return { isValid: true, firstError: null, field: null };
};

// ============================================================================
// PRE-BUILT SCHEMAS
// ============================================================================

/**
 * Pre-built validation schemas for common forms.
 */
export const schemas = {
  /**
   * Custom Agent creation/editing
   */
  customAgent: {
    name: [
      validators.required,
      validators.minLength(2),
      validators.maxLength(50),
      validators.alphanumericSpaces,
    ],
    system_prompt: [
      validators.required,
      validators.minLength(10),
      validators.maxLength(5000),
    ],
    description: [validators.maxLength(200)],
  },

  /**
   * Coupon creation
   */
  coupon: {
    code: [
      validators.required,
      validators.minLength(4),
      validators.maxLength(20),
      validators.alphanumeric,
    ],
    credits: [
      validators.required,
      validators.integer,
      validators.positive,
      validators.max(10000),
    ],
    max_uses: [validators.integer, validators.positive],
  },

  /**
   * Prompt saving
   */
  prompt: {
    title: [
      validators.required,
      validators.minLength(3),
      validators.maxLength(100),
    ],
    content: [
      validators.required,
      validators.minLength(10),
      validators.maxLength(50000),
    ],
    category: [validators.required],
  },

  /**
   * User profile update
   */
  profile: {
    display_name: [
      validators.minLength(2),
      validators.maxLength(50),
      validators.alphanumericSpaces,
    ],
    bio: [validators.maxLength(500)],
  },

  /**
   * Login form
   */
  login: {
    email: [validators.required, validators.email],
    password: [validators.required, validators.minLength(6)],
  },

  /**
   * Sign up form
   */
  signup: {
    email: [validators.required, validators.email],
    password: [
      validators.required,
      validators.minLength(8),
      validators.pattern(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain uppercase, lowercase, and number",
      ),
    ],
    confirmPassword: [validators.required],
  },

  /**
   * Credit adjustment (admin)
   */
  creditAdjustment: {
    amount: [
      validators.required,
      validators.integer,
      validators.between(-10000, 10000),
    ],
    reason: [
      validators.required,
      validators.minLength(5),
      validators.maxLength(200),
    ],
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Sanitize a string by removing potentially dangerous characters.
 *
 * @param {string} value - String to sanitize
 * @returns {string} Sanitized string
 */
export const sanitize = (value) => {
  if (typeof value !== "string") return value;
  return value
    .replace(/[<>]/g, "") // Remove angle brackets
    .trim();
};

/**
 * Sanitize all string values in an object.
 *
 * @param {Object} obj - Object to sanitize
 * @returns {Object} Sanitized object
 */
export const sanitizeObject = (obj) => {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "string") {
      result[key] = sanitize(value);
    } else if (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      result[key] = sanitizeObject(value);
    } else {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Normalize an email address (lowercase, trim).
 *
 * @param {string} email - Email to normalize
 * @returns {string} Normalized email
 */
export const normalizeEmail = (email) => {
  if (typeof email !== "string") return email;
  return email.toLowerCase().trim();
};

/**
 * Check if passwords match.
 *
 * @param {string} password - Password
 * @param {string} confirmPassword - Confirmation
 * @returns {boolean} Whether they match
 */
export const passwordsMatch = (password, confirmPassword) => {
  return password === confirmPassword;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  validators,
  validateField,
  validateSchema,
  validateFirst,
  schemas,
  sanitize,
  sanitizeObject,
  normalizeEmail,
  passwordsMatch,
};
